import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { submitLead, formatApiErrorDetail, LeadInput } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePageMeta } from "@/lib/meta";
import { BUDGET_BANDS, CITIES, PROPERTY_TYPES, SITE, TIMELINES, waLink } from "@/lib/site";
import { WhatsAppIcon } from "@/components/Footer";

const NEXT_STEPS = [
  { n: "01", t: "We call within a working day", d: "A real person from the studio — not a call centre — to understand your home and timeline." },
  { n: "02", t: "We map space & budget honestly", d: "If your band doesn't fit the scope you want, we'll say so on the first call." },
  { n: "03", t: "You approve a 3D you can trust", d: "Built with real material textures, priced by a fixed BOQ before execution begins." },
];

const inputCls =
  "w-full border border-white/15 bg-obsidian-card px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 transition-colors focus:border-gold focus:outline-none";

export default function Enquire() {
  usePageMeta(
    "Enquire — Start Your Turnkey Project | Shriya Redkar Interiors",
    "Tell us your city, property type, budget band and timeline. We'll call within a working day — and hand you straight to WhatsApp if you prefer."
  );
  const [params] = useSearchParams();
  const preselectCity = useMemo(() => {
    const c = params.get("city");
    return CITIES.some((x) => x.name === c) ? c! : "";
  }, [params]);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: preselectCity,
    property_type: "",
    budget_band: "",
    timeline: "",
    message: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState<{ whatsapp_url: string } | null>(null);
  const { user } = useAuth();

  // Signed-in visitors get name/email pre-filled (only where still blank).
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
  }, [user]);

  const set = (k: keyof typeof form, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const valid = () => {
    if (form.name.trim().length < 2) return "Please enter your name.";
    if (form.phone.replace(/\D/g, "").length < 10) return "Please enter a valid 10-digit phone number.";
    if (!form.city) return "Please choose your city.";
    if (!form.property_type) return "Please choose your property type.";
    if (!form.budget_band) return "Please choose a budget band.";
    if (!form.timeline) return "Please choose a timeline.";
    if (!form.consent) return "Please allow us to save your enquiry so the team can call you back.";
    return "";
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = valid();
    if (v) {
      setError(v);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const payload: LeadInput = { ...form, message: form.message };
      const res = await submitLead(payload);
      setSuccess(res);
      window.open(res.whatsapp_url, "_blank", "noopener");
    } catch (e: any) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <main className="flex min-h-screen items-center pt-[76px]">
        <div className="container-x py-16">
          <div className="mx-auto max-w-2xl border border-gold/40 bg-obsidian-card p-10 text-center sm:p-14" data-testid="enquiry-success-panel">
            <p className="eyebrow">Enquiry received</p>
            <h1 className="mt-5 font-serif text-4xl font-light text-zinc-50">
              Thank you, {form.name.split(" ")[0]}.
            </h1>
            <p className="mt-5 text-base leading-relaxed text-zinc-400">
              Your brief is saved with the studio and routed to the right city team. One more step makes it
              instant — continue on WhatsApp and your details arrive already typed.
            </p>
            <a
              href={success.whatsapp_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-direct-link-button"
              className="mt-9 inline-flex items-center justify-center gap-3 bg-wa px-8 py-4 font-display text-[13px] font-semibold uppercase tracking-[0.18em] text-obsidian transition-all hover:brightness-110"
            >
              <WhatsAppIcon className="h-5 w-5" /> Continue on WhatsApp
            </a>
            <p className="mt-6 text-xs text-zinc-500">
              Prefer a call instead? The studio will ring {form.phone} within a working day.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-[76px]">
      <section className="container-x grid gap-14 py-16 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="eyebrow">Start your project</p>
            <h1 className="mt-5 font-serif text-4xl font-light leading-[1.08] text-zinc-50 sm:text-5xl">
              Tell us the truth about your budget.
              <span className="block italic text-gold">We'll match it with honesty.</span>
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-400">
              The brief below routes you to the right city team — so your first call is with people who
              build in your neighbourhood, not a generic salesman.
            </p>
            <div className="mt-12 space-y-8">
              {NEXT_STEPS.map((s) => (
                <div key={s.n} className="flex gap-5">
                  <span className="font-mono text-xs text-gold">{s.n}</span>
                  <div>
                    <p className="font-serif text-xl text-zinc-100">{s.t}</p>
                    <p className="mt-1 text-sm leading-relaxed text-zinc-400">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-12 text-sm text-zinc-500">
              Prefer to talk first? Call{" "}
              <a href={`tel:${SITE.phoneHref}`} data-testid="enquire-phone-link" className="text-gold">
                {SITE.phoneDisplay}
              </a>
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            noValidate
            data-testid="enquiry-form"
            className="border border-white/10 bg-obsidian-soft p-7 sm:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="f-name" className="mb-2 block font-display text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Full name *
                </label>
                <input
                  id="f-name"
                  data-testid="enquiry-input-name"
                  className={inputCls}
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="f-phone" className="mb-2 block font-display text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Phone / WhatsApp *
                </label>
                <input
                  id="f-phone"
                  data-testid="enquiry-input-phone"
                  className={inputCls}
                  placeholder="98XXX XXXXX"
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="f-email" className="mb-2 block font-display text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Email <span className="normal-case tracking-normal text-zinc-600">(optional — for your enquiry status)</span>
                </label>
                <input
                  id="f-email"
                  type="email"
                  data-testid="enquiry-input-email"
                  className={inputCls}
                  placeholder="you@example.com"
                  inputMode="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="f-city" className="mb-2 block font-display text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  City *
                </label>
                <select
                  id="f-city"
                  data-testid="enquiry-select-city"
                  className={inputCls}
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                >
                  <option value="">Select city</option>
                  {CITIES.map((c) => (
                    <option key={c.slug} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="f-type" className="mb-2 block font-display text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Property type *
                </label>
                <select
                  id="f-type"
                  data-testid="enquiry-select-property"
                  className={inputCls}
                  value={form.property_type}
                  onChange={(e) => set("property_type", e.target.value)}
                >
                  <option value="">Select property type</option>
                  {PROPERTY_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6">
              <p className="mb-2 font-display text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Budget band * <span className="normal-case tracking-normal text-zinc-600">(interiors budget, excluding property)</span>
              </p>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Budget band">
                {BUDGET_BANDS.map((b) => (
                  <button
                    type="button"
                    key={b}
                    data-testid={`enquiry-budget-${b.replace(/[₹–+]/g, "")}`}
                    onClick={() => set("budget_band", b)}
                    className={`border px-5 py-2.5 text-sm transition-all duration-300 ${
                      form.budget_band === b
                        ? "border-gold bg-gold text-obsidian"
                        : "border-white/15 text-zinc-300 hover:border-gold/60"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="f-timeline" className="mb-2 block font-display text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                When do you want to start? *
              </label>
              <select
                id="f-timeline"
                data-testid="enquiry-select-timeline"
                className={inputCls}
                value={form.timeline}
                onChange={(e) => set("timeline", e.target.value)}
              >
                <option value="">Select timeline</option>
                {TIMELINES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-6">
              <label htmlFor="f-message" className="mb-2 block font-display text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Your vision (optional)
              </label>
              <textarea
                id="f-message"
                data-testid="enquiry-input-message"
                rows={4}
                className={inputCls}
                placeholder="A 3BHK in Seawoods — warm woods, no glossy whites, handover before Diwali…"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
              />
            </div>

            <label className="mt-7 flex cursor-pointer items-start gap-3 text-sm text-zinc-400" data-testid="enquiry-consent-wrapper">
              <input
                type="checkbox"
                data-testid="enquiry-consent-checkbox"
                checked={form.consent}
                onChange={(e) => set("consent", e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#C5A059]"
              />
              <span>
                I agree that {SITE.name} may save these details to contact me about my enquiry. Nothing is
                published or shared — we collect the minimum needed to call you back. *
              </span>
            </label>

            {error && (
              <p className="mt-5 border-l-2 border-red-500 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="enquiry-error-message">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="enquiry-form-submit-button"
              className="btn-gold mt-8 w-full disabled:cursor-wait disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send my enquiry"}
            </button>
            <p className="mt-4 flex items-center justify-center gap-2 text-center text-xs text-zinc-500">
              or finish on WhatsApp instantly <ArrowUpRight className="h-3 w-3 text-gold" />
            </p>
          </form>
        </Reveal>
      </section>
    </main>
  );
}
